import { ReactNode } from 'react';

import { TourKeys } from '@cast/constants';
import { mergeSx, SxProps, Typography, Box, Stack } from '@cast/design-system';

import { ClusterDemoStatusChip } from 'components/chips';
import { OnboardClusterButton } from 'components/clickable-actions';
import { globalTourSteps } from 'constants/tour';
import { useAbility } from '@cast/shared-ability-data-access';
import { GlobalTourStep } from 'core/tour';
import { useIsTourActive } from 'core/tour-v2/hooks/useIsTourActive';
import { ContentContainer } from 'main-layout/ContentContainer';

type Props = {
  title?: string;
  labelText?: string;
  actions?: ReactNode;
  showMainAction?: boolean;
  sx?: SxProps;
};

export const DemoClusterHeader = ({
  title = 'Get free cost monitoring, security and optimization insights for K8s',
  labelText,
  actions,
  showMainAction = true,
  sx,
}: Props) => {
  const ability = useAbility();

  const isOpTour = useIsTourActive(TourKeys.OpinionatedPlatform);
  if (isOpTour) {
    return;
  }

  return (
    <Box
      sx={mergeSx(
        {
          height: 48,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',

          '&:before': {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            content: '""',
            boxShadow: '0px 0px 24px 10px rgb(178 237 255 / 60%)',
          },
        },
        sx
      )}
      data-testid="demo-cluster-header"
    >
      <ContentContainer>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap="24px">
            <ClusterDemoStatusChip label={labelText} />

            {title && (
              <Typography variant="A1" color="aqua.800">
                {title}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" gap={24}>
            {actions}
            {showMainAction && (
              <GlobalTourStep
                step={globalTourSteps.connectMyCluster}
                highlight="bubble"
                placement="top-end"
                beaconPosition={{ right: -8, top: 6 }}
              >
                <OnboardClusterButton
                  disabled={ability.cannot('update', 'Clusters')}
                  variant="secondary"
                  size="sm"
                >
                  Connect cluster
                </OnboardClusterButton>
              </GlobalTourStep>
            )}
          </Stack>
        </Stack>
      </ContentContainer>
    </Box>
  );
};
